import type {
  CompletionQuizQuestion,
  CourseCompletionQuizStatus,
  CourseCompletionQuizSummary,
} from "@/types/courseCompletionQuiz";

/** The statuses that have a finished run to look back at. */
export const hasFinishedRun = (status: CourseCompletionQuizStatus): boolean =>
  status === "waiting" || status === "failed_final" || status === "passed";

/**
 * Where a learner is sent for the course completion quiz: the introduction
 * before a run, the open run to continue it, the result once a run has ended.
 */
export const courseCompletionQuizPath = (
  enrollmentId: string,
  status: CourseCompletionQuizStatus
): string => {
  const base = `/course/${enrollmentId}/final-quiz`;
  if (status === "in_progress") return `${base}/run`;
  if (hasFinishedRun(status)) return `${base}/result`;
  return base;
};

/**
 * Whether a question still has to be answered: it has no answer yet, or its
 * answer could not be graded and has to be sent again.
 */
export const needsAnswer = (question: CompletionQuizQuestion): boolean =>
  !question.answer || question.answer.status === "failed";

export type ResultScreen = "show" | "wait" | "intro" | "dashboard";

/**
 * What the result page shows. The result is the last finished run, so while a
 * newer run is still being graded it belongs to an earlier attempt and is not
 * shown; the page waits instead. Without any run there is no result to wait for.
 */
export const resultScreenFor = (
  summary: Pick<CourseCompletionQuizSummary, "status" | "attemptsUsed">,
  result: { attemptNumber: number } | null
): ResultScreen => {
  if (summary.status === "locked") return "dashboard";
  if (summary.status === "startable") return result ? "show" : "intro";
  if (result && result.attemptNumber >= summary.attemptsUsed) return "show";
  return "wait";
};
